import { numericValues } from 'utils/helpers';

export const getYCurrentLossHelper = (
  value1: number | string,
  value2: number | string,
) => (Number(value1) - Number(value2)).toFixed(2);

export const getNotModifiedTotalMtha = (
  CECSoil: number,
  FineAndDry: number,
  NV: number,
  TargetStatus: number,
  CurrentStatus: number,
  Dose: number,
) => {
  const soilWithFineAndDry = ((CECSoil * 150) / 3500) * FineAndDry;

  const nvAndSoil = soilWithFineAndDry / (NV * 10);

  const result = (nvAndSoil / 0.5) * ((TargetStatus - CurrentStatus) * Dose);

  return result.toFixed(2);
};

export const getModifiedTotalMtha = (
  currentSec: string | number,
  currentPh: string | number,
  CECSoil: number,
  FineAndDry: number,
  NV: number,
  TargetStatus: number,
  Dose: number,
) => {
  // Don't round early - keep full precision
  const getCalculatedCurrentPh = Math.atan((Math.round(Number(currentSec)) - 70) / 54) * 1.45 + 5.8;

  // Fix order of operations - need parentheses around (TargetStatus - Number(currentPh))
  const isOk = ((TargetStatus - getCalculatedCurrentPh) * Dose)
    + ((TargetStatus - Number(currentPh)) * Dose);

  if (isOk < 0) {
    return '0.00';
  }

  const onePart = (((CECSoil * 150 / 3500) * FineAndDry) / (NV * 10)) / 0.5;
  // Keep full precision in calculations, round only at the end
  const twoPart = ((TargetStatus - getCalculatedCurrentPh + (TargetStatus - Number(currentPh))) * Dose) / 2;

  // Round only at the very end
  return +(onePart * twoPart).toFixed(2);
};

export const getP39Value = (S_CECSoil: number, NV: number, Dose: number) => {
  const left = S_CECSoil * 675.96 * Math.pow(S_CECSoil, -1.405) * 0.08 * 10;

  return Math.round((left / NV) * 10) * 10 * Dose;
};

export const getFineDrySoilFirst = (
  v33: number,
  mth: number,
  service: number,
) => {
  const result = v33 * (mth + service);

  return result;
};

export const getFineDrySoilSecond = (
  P39: number,
  Service: number,
  cost: number,
) => (P39 / 1000) * (Service + cost);

export const getYCurrentProfitHelper = (
  currentPh: number | string,
  targetPh: number | string,
  dose: number | string,
  split: number,
) => {
  const DOSE = Number(numericValues(String(dose))) / 100;
  const rightSide = (Number(targetPh) - Number(currentPh)) * (DOSE / split);

  // return (Number(currentPh) + rightSide).toFixed(2);
  return String(Math.round((Number(currentPh) + rightSide) * 100) / 100);
};

export const getYCurrentProfitNoHelper = (
  currentPh: number | string,
  targetPh: number | string,
  dose: number | string,
  split: number | string,
) => {
  const DOSE = Number(numericValues(String(dose))) / 100;
  const rightSide =
    (Number(targetPh) - Number(currentPh)) * (DOSE / Number(split));

  return Number(currentPh) + rightSide;
};

export const getEfficiencyFormula = (
  AG7: number,
  AG12: number,
  AG13: number,
  AG17: number,
  AG20: number,
) => {
  const result =
    (200 / Math.PI) *
    Math.atan(
      399 *
      (AG7 / 12) *
      ((Math.pow(2, (5 - AG12) / 0.7) +
        Math.pow(2, (5 - (Math.atan((AG13 - 70) / 54) * 1.45 + 5.8)) / 0.7)) /
        2) *
      Math.pow(2, (AG17 - 150) / 18) *
      Math.pow(2, (AG20 - 100) / (100 / 3)),
    );

  return result;
};

const d23lossGetFirst = (D5: number, D6: number) => {
  const value1 = D5 > 7 ? 0 : 0.0244 * Math.pow(D5, 2) - 0.3876 * D5 + 1.528;
  const value2 = D6 > 7 ? 0 : 0.0244 * Math.pow(D6, 2) - 0.3876 * D6 + 1.528;

  return value1 - value2;
};

const d23lossGetSecond = (D5: number, D6: number) => {
  const value1 = D5 > 7 ? 0 : 0.0168 * Math.pow(D5, 2) - 0.3026 * D5 + 1.3089;
  const value2 = D6 > 7 ? 0 : 0.0168 * Math.pow(D6, 2) - 0.3026 * D6 + 1.3089;

  return value1 - value2;
};

const d23lossGet3 = (D5: number, D6: number) => {
  const value1 =
    D5 > 7.25 ? 0 : 0.018 * Math.pow(D5, 2) - 0.3169 * D5 + 1.3647;
  const value2 =
    D6 > 7.25 ? 0 : 0.018 * Math.pow(D6, 2) - 0.3169 * D6 + 1.3647;

  return value1 - value2;
};

const d23lossGet4 = (D5: number, D6: number) => {
  const value1 = D5 > 7.5 ? 0 : 0.0183 * Math.pow(D5, 2) - 0.3223 * D5 + 1.4033;
  const value2 = D6 > 7.5 ? 0 : 0.0183 * Math.pow(D6, 2) - 0.3223 * D6 + 1.4033;

  return value1 - value2;
};

const d23lossGet5 = (D5: number, D6: number) => {
  const value1 = D5 > 7 ? 0 : 0.0139 * Math.pow(D5, 2) - 0.2875 * D5 + 1.3396;
  const value2 = D6 > 7.5 ? 0 : 0.0139 * Math.pow(D6, 2) - 0.2875 * D6 + 1.3396;

  return value1 - value2;
};

export const d23loss = (
  cropGroupYield: number,
  getY1CurrentLoss: number,
  getY1TargetLoss: number,
) => {
  if (cropGroupYield === 1) {
    return d23lossGetFirst(getY1CurrentLoss, getY1TargetLoss);
  }

  if (cropGroupYield === 2) {
    return d23lossGetSecond(getY1CurrentLoss, getY1TargetLoss);
  }

  if (cropGroupYield === 3) {
    return d23lossGet3(getY1CurrentLoss, getY1TargetLoss);
  }

  if (cropGroupYield === 4) {
    return d23lossGet4(getY1CurrentLoss, getY1TargetLoss);
  }

  if (cropGroupYield === 5) {
    return d23lossGet5(getY1CurrentLoss, getY1TargetLoss);
  }
};

const d23Profit1 = (getY1Currentloss: number, getY1TargetProfit: number) => {
  const value1 =
    getY1Currentloss > 7
      ? 0
      : 0.0244 * Math.pow(getY1Currentloss, 2) -
      0.3876 * getY1Currentloss +
      1.528;
  const value2 =
    getY1TargetProfit > 7
      ? 0
      : 0.0244 * Math.pow(getY1TargetProfit, 2) -
      0.3876 * getY1TargetProfit +
      1.528;

  return value1 - value2;
};

const d23Profit2 = (getY1Currentloss: number, getY1TargetProfit: number) => {
  const value1 =
    getY1Currentloss > 7
      ? 0
      : 0.0168 * Math.pow(getY1Currentloss, 2) -
      0.3026 * getY1Currentloss +
      1.3089;
  const value2 =
    getY1TargetProfit > 7
      ? 0
      : 0.0168 * Math.pow(getY1TargetProfit, 2) -
      0.3026 * getY1TargetProfit +
      1.3089;

  return value1 - value2;
};

const d23Profit3 = (getY1Currentloss: number, getY1TargetProfit: number) => {
  const value1 =
    getY1Currentloss > 7.25
      ? 0
      : 0.018 * Math.pow(getY1Currentloss, 2) -
      0.3169 * getY1Currentloss +
      1.3647;
  const value2 =
    getY1TargetProfit > 7.25
      ? 0
      : 0.018 * Math.pow(getY1TargetProfit, 2) -
      0.3169 * getY1TargetProfit +
      1.3647;

  return value1 - value2;
};

const d23Profit4 = (getY1Currentloss: number, getY1TargetProfit: number) => {
  const value1 =
    getY1Currentloss > 7.5
      ? 0
      : 0.0183 * Math.pow(getY1Currentloss, 2) -
      0.3223 * getY1Currentloss +
      1.4033;
  const value2 =
    getY1TargetProfit > 7.5
      ? 0
      : 0.0183 * Math.pow(getY1TargetProfit, 2) -
      0.3223 * getY1TargetProfit +
      1.4033;

  return value1 - value2;
};

const d23Profit5 = (getY1Currentloss: number, getY1TargetProfit: number) => {
  const value1 =
    getY1Currentloss > 7
      ? 0
      : 0.0139 * Math.pow(getY1Currentloss, 2) -
      0.2875 * getY1Currentloss +
      1.3396;
  const value2 =
    getY1TargetProfit > 7.5
      ? 0
      : 0.0139 * Math.pow(getY1TargetProfit, 2) -
      0.2875 * getY1TargetProfit +
      1.3396;

  return value1 - value2;
};

export const d23profit = (
  cropGroupYield: number,
  getY1Currentloss: number,
  getY1TargetProfit: number,
) => {
  if (cropGroupYield === 1) {
    return d23Profit1(getY1Currentloss, getY1TargetProfit);
  }

  if (cropGroupYield === 2) {
    return d23Profit2(getY1Currentloss, getY1TargetProfit);
  }

  if (cropGroupYield === 3) {
    return d23Profit3(getY1Currentloss, getY1TargetProfit);
  }

  if (cropGroupYield === 4) {
    return d23Profit4(getY1Currentloss, getY1TargetProfit);
  }

  if (cropGroupYield === 5) {
    return d23Profit5(getY1Currentloss, getY1TargetProfit);
  }
};

export const gainsOmhelper = (
  FineAndDry: number,
  OM: number,
  N_rate: number,
  P2O5_rate: number,
  SO3_rate: number,
  Soil_ph_achieved_Y1: number,
  Soil_Ph_evolution_Y1: number,
  N_from_NPK_Prices: number,
  P2O5_from_NPK_Prices: number,
  SO3_price: number,
): number => {
  const N_component =
    FineAndDry *
    10 *
    OM *
    N_rate *
    ((0.009 * Soil_ph_achieved_Y1 - 0.041) * 100 -
      (0.009 * Soil_Ph_evolution_Y1 - 0.041) * 100) *
    N_from_NPK_Prices;

  const P2O5_component =
    FineAndDry *
    10 *
    OM *
    P2O5_rate *
    ((0.009 * Soil_ph_achieved_Y1 - 0.041) * 100 -
      (0.009 * Soil_Ph_evolution_Y1 - 0.041) * 100) *
    P2O5_from_NPK_Prices;
  const SO3_component =
    FineAndDry *
    10 *
    OM *
    SO3_rate *
    ((0.009 * Soil_ph_achieved_Y1 - 0.041) * 100 -
      (0.009 * Soil_Ph_evolution_Y1 - 0.041) * 100) *
    SO3_price;

  return N_component + P2O5_component + SO3_component;
};

const yieldGain1 = (U24: number, Y24: number) => {
  const condition1 =
    U24 > 7 ? 0 : 0.0244 * Math.pow(U24, 2) - 0.3876 * U24 + 1.528;
  const condition2 =
    Y24 > 7 ? 0 : 0.0244 * Math.pow(Y24, 2) - 0.3876 * Y24 + 1.528;

  return ((condition1 - condition2) * 100).toFixed(1);
};

const yieldGain2 = (U24: number, Y24: number) => {
  const condition1 =
    U24 > 7 ? 0 : 0.0168 * Math.pow(U24, 2) - 0.3026 * U24 + 1.3089;
  const condition2 =
    Y24 > 7 ? 0 : 0.0168 * Math.pow(Y24, 2) - 0.3026 * Y24 + 1.3089;

  return ((condition1 - condition2) * 100).toFixed(1);
};

const yieldGain3 = (U24: number, Y24: number) => {
  const condition1 =
    U24 > 7.25 ? 0 : 0.018 * Math.pow(U24, 2) - 0.3169 * U24 + 1.3647;
  const condition2 =
    Y24 > 7.25 ? 0 : 0.018 * Math.pow(Y24, 2) - 0.3169 * Y24 + 1.3647;

  return ((condition1 - condition2) * 100).toFixed(1);
};

const yieldGain4 = (U24: number, Y24: number) => {
  const condition1 =
    U24 > 7.5 ? 0 : 0.0183 * Math.pow(U24, 2) - 0.3223 * U24 + 1.4033;
  const condition2 =
    Y24 > 7.5 ? 0 : 0.0183 * Math.pow(Y24, 2) - 0.3223 * Y24 + 1.4033;

  return ((condition1 - condition2) * 100).toFixed(1);
};

const yieldGain5 = (U24: number, Y24: number) => {
  const condition1 =
    U24 > 7 ? 0 : 0.0139 * Math.pow(U24, 2) - 0.2875 * U24 + 1.3396;
  const condition2 =
    Y24 > 7.5 ? 0 : 0.0139 * Math.pow(Y24, 2) - 0.2875 * Y24 + 1.3396;

  return ((condition1 - condition2) * 100).toFixed(1);
};

export const yieldPercentHelper = (
  GroupYeildLosses: number,
  U24: number,
  Y24: number,
) => {
  if (GroupYeildLosses === 1) {
    return yieldGain1(U24, Y24);
  }

  if (GroupYeildLosses === 2) {
    return yieldGain2(U24, Y24);
  }

  if (GroupYeildLosses === 3) {
    return yieldGain3(U24, Y24);
  }

  if (GroupYeildLosses === 4) {
    return yieldGain4(U24, Y24);
  }

  if (GroupYeildLosses === 5) {
    return yieldGain5(U24, Y24);
  }

  return '0';
};

export const yieldPriceHelper = (
  PotentialYeild: number,
  Price: number,
  yieldPercent: number,
) => Math.round(PotentialYeild * Price * yieldPercent);

export const npkGainsCropHelper = (
  n: number,
  p: number,
  k: number,
  nPrice: number,
  pPrice: number,
  kPrice: number,
  phIfNoLim: number,
  targetPh: number,
) => {
  function calculateN(phIfNoLim: number, targetPh: number) {
    if (phIfNoLim > 6.5) {
      return 0;
    } else {
      if (targetPh < 6.501) {
        return (
          1 - (30 * phIfNoLim - 95) / 100 - (1 - (30 * targetPh - 95) / 100)
        );
      } else {
        return 1 - (30 * phIfNoLim - 95) / 100 - (1 - (30 * 6.5 - 95) / 100);
      }
    }
  }

  const alln = n * calculateN(phIfNoLim, targetPh) * nPrice;

  function calculateP2O5(phIfNoLim: number, targetPh: number) {
    if (phIfNoLim > 6.5) {
      return 0;
    } else {
      if (targetPh < 6.501) {
        return (
          1 -
          (35 * phIfNoLim - 127.5) / 100 -
          (1 - (35 * targetPh - 127.5) / 100)
        );
      } else {
        return (
          1 - (35 * phIfNoLim - 127.5) / 100 - (1 - (35 * 6.5 - 127.5) / 100)
        );
      }
    }
  }

  const allp = p * calculateP2O5(phIfNoLim, targetPh) * pPrice;

  function calculateK2O(phIfNoLim: number, targetPh: number) {
    if (phIfNoLim > 6) {
      return 0;
    } else {
      if (targetPh < 6.001) {
        return (
          1 - (40 * phIfNoLim - 140) / 100 - (1 - (40 * targetPh - 140) / 100)
        );
      } else {
        return 1 - (40 * phIfNoLim - 140) / 100 - (1 - (40 * 6 - 140) / 100);
      }
    }
  }

  const allk = k * calculateK2O(phIfNoLim, targetPh) * kPrice;

  return alln + allp + allk;
};

export const MONPShelper = (
  FineAndDry: number,
  OM: number,
  targetPh: number,
  phIfNoLimApprov: number,
  NRate: number,
  P2ORate: number,
  K2ORate: number,
  priceN: number,
  priceP2O5: number,
  priceK2O: number,
) => {
  const valueN = (
    FineAndDry *
    10 *
    OM *
    NRate *
    ((0.009 * targetPh - 0.041) * 100 -
      (0.009 * phIfNoLimApprov - 0.041) * 100) *
    priceN
  ).toFixed(1);

  const valueP2O5 = (
    FineAndDry *
    10 *
    OM *
    P2ORate *
    ((0.009 * targetPh - 0.041) * 100 -
      (0.009 * phIfNoLimApprov - 0.041) * 100) *
    priceP2O5
  ).toFixed(2);

  const valueK2O = (
    FineAndDry *
    10 *
    OM *
    K2ORate *
    ((0.009 * targetPh - 0.041) * 100 -
      (0.009 * phIfNoLimApprov - 0.041) * 100) *
    priceK2O
  ).toFixed(1);

  return Math.round(Number(valueN) + Number(valueP2O5) + Number(valueK2O));
};
